import React, { useContext, useMemo } from "react";
import { ArrowLeft, ArrowRight, BarChart3, TrendingUp } from "lucide-react";
import { ThemeContext } from "../../../../constant/theme";
import { LanguageContext } from "../../../../constant/LanguageContext";
import { useAuth } from "../../../../context/AuthContext";
import text from "../../constants/reportsTranslations";
import { SkeletonBox } from "../../../../components/SkeletonLoader";
import DataTable from "../../../../components/DataTable/DataTable";
import BarChart from "../../../../components/charts/BarChart";
import AreaChart from "../../../../components/charts/AreaChart";
import ExportMenu from "../export/ExportMenu";
import EmptyState from "../states/EmptyState";
import ErrorState from "../states/ErrorState";
import { getDistributions, getSummaryCards, getTrendSeries, humanizeKey, baseDistributionKey } from "../../utils/chartDataHelpers";

const ReportPreviewPage = ({
  category, rows, summary, title, tableLoading, tableError, onRetry,
  onBack, onExcel, onPdf, onEmail, onSchedule, onExportSelected, onVisibleColumnsChange,
}) => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const { isColumnVisible } = useAuth();
  const isDark = theme === "dark";
  const isRTL = language === "ar";
  const BackIcon = isRTL ? ArrowRight : ArrowLeft;

  const distributions = useMemo(() => getDistributions(summary), [summary]);
  const summaryCards = useMemo(() => getSummaryCards(summary), [summary]);
  const trend = useMemo(() => getTrendSeries(rows), [rows]);

  // Chart titles/subtitles describe exactly what each chart shows and are
  // built entirely from the live `summary`/`rows` payload the report API
  // returned (chartDataHelpers.getDistributions / getTrendSeries) -- there is
  // no mock or hardcoded chart data anywhere in this page. A chart section
  // simply doesn't render when the API returns nothing to show.
  const primaryDistribution = distributions[0] || null;
  // Only show a second breakdown chart when there IS a genuinely distinct
  // distribution to show -- falling back to the same data as the first chart
  // just duplicated it under a mismatched title.
  const secondaryDistribution = category?.id !== "leave" ? (distributions[1] || distributions[2] || null) : null;

  // e.g. "Department Distribution", "Employment Type Distribution" -- the
  // dimension name comes from the real summary key, run through the shared
  // fieldLabels dictionary (falling back to a humanized version of the key).
  const distributionTitle = (dist) => {
    if (!dist) return "";
    // Strip the "...Distribution"/"...Breakdown" suffix the backend uses for
    // these summary keys BEFORE looking it up / humanizing it, then let the
    // template add that word back exactly once (was rendering doubled, e.g.
    // "Status Distribution Distribution").
    const baseKey = baseDistributionKey(dist.key);
    const label = text[language].fieldLabels?.[baseKey] || humanizeKey(baseKey);
    return (text[language].previewDistributionTemplate || "{label}").replace("{label}", label);
  };
  // e.g. "6 groups" -- a live count of the segments actually returned, so the
  // header always reflects the current filtered data set, not a fixed label.
  const distributionSubtitle = (dist) => (dist ? `${dist.data.length} ${text[language].previewGroups}` : "");
  // e.g. "2026-01 – 2026-07" -- the real first/last buckets from the trend series.
  const trendSubtitle = trend.length > 1 ? `${trend[0].name} – ${trend[trend.length - 1].name}` : trend[0]?.name || "";

  const columns = useMemo(() => {
    if (!rows.length) return [];
    const labels = text[language].fieldLabels || {};
    const tableKey = `reportsTable_${category?.id}`;
    return Object.keys(rows[0])
      .filter((k) => typeof rows[0][k] !== "object")
      .filter((k) => isColumnVisible?.("reports", tableKey, k) !== false)
      .map((k) => ({ id: k, label: labels[k] || k.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()) }));
  }, [rows, language, category?.id, isColumnVisible]);

  if (tableError) {
    return (
      <ErrorState
        theme={theme} isRTL={isRTL}
        title={text[language].errorStateTitle}
        description={text[language].errorStateDesc}
        retryLabel={text[language].errorStateRetry}
        onRetry={onRetry}
      />
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, background: "none", border: "none", cursor: "pointer", color: isDark ? "#93c5fd" : "#2563eb", fontSize: 13, fontWeight: 600, flexDirection: isRTL ? "row-reverse" : "row" }}
            >
              <BackIcon size={16} /> {text[language].backToCategory}
            </button>
          )}
          <h2 style={{ fontSize: 20, fontWeight: 800, color: isDark ? "#f8fafc" : "#0f172a", margin: 0 }}>{category?.name || title}</h2>
        </div>
        <ExportMenu onExcel={onExcel} onPdf={onPdf} onEmail={onEmail} onSchedule={onSchedule} theme={theme} isRTL={isRTL} text={text} language={language} />
      </div>

      {tableLoading ? (
        <div style={{ display: "grid", gap: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
            {Array.from({ length: 4 }).map((_, i) => <SkeletonBox key={i} width="100%" height="80px" style={{ borderRadius: 14 }} />)}
          </div>
          <SkeletonBox width="100%" height="280px" style={{ borderRadius: 16 }} />
          <SkeletonBox width="100%" height="320px" style={{ borderRadius: 16 }} />
        </div>
      ) : rows.length === 0 && summaryCards.every((c) => !c.value) ? (
        <EmptyState theme={theme} isRTL={isRTL} title={text[language].emptyStateTitle} description={text[language].emptyStateDesc} />
      ) : (
        <>
          {/* Summary / Totals */}
          {summaryCards.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 24 }}>
              {summaryCards.map((c) => (
                <div key={c.key} style={{ padding: "14px 16px", borderRadius: 14, background: isDark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)"}` }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: isDark ? "rgba(255,255,255,0.5)" : "rgba(30,41,59,0.5)", textTransform: "capitalize", marginBottom: 6 }}>
                    {text[language].fieldLabels?.[c.key] || c.key.replace(/([A-Z])/g, " $1")}
                  </div>
                  <div style={{ fontSize: 19, fontWeight: 800, color: isDark ? "#f8fafc" : "#0f172a" }}>
                    {c.key === "dateRange" ? String(c.value).replace(" to ", " – ") : String(c.value)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Charts -- every header is built from the live report data (real
              dimension name + a live segment/date-range count pulled straight
              from the API response), and each card's icon/accent picks up the
              exact color used by the chart it sits above. No mock data. */}
          {category?.id !== "company" && (primaryDistribution || trend.length > 0 || secondaryDistribution) && (
            <div style={{ marginBottom: 24 }}>
              <h3 style={sectionTitleStyle(isDark, isRTL)}>{text[language].previewCharts}</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(340px, 100%), 1fr))", gap: 16 }}>
                {primaryDistribution && (
                  <ChartCard
                    title={distributionTitle(primaryDistribution)}
                    subtitle={distributionSubtitle(primaryDistribution)}
                    icon={BarChart3}
                    color={category?.color || "#3b82f6"}
                    isDark={isDark}
                  >
                    <BarChart data={primaryDistribution.data} theme={theme} color={category?.color || "#3b82f6"} noDataLabel={text[language].previewNoChartData} />
                  </ChartCard>
                )}
                {trend.length > 0 && (
                  <ChartCard
                    title={text[language].previewTrendOverTime}
                    subtitle={trendSubtitle}
                    icon={TrendingUp}
                    color={category?.color || "#8b5cf6"}
                    isDark={isDark}
                  >
                    <AreaChart data={trend} theme={theme} color={category?.color || "#8b5cf6"} noDataLabel={text[language].previewNoChartData} />
                  </ChartCard>
                )}
                {secondaryDistribution && (
                  <ChartCard
                    title={distributionTitle(secondaryDistribution)}
                    subtitle={distributionSubtitle(secondaryDistribution)}
                    icon={BarChart3}
                    color="#10b981"
                    isDark={isDark}
                  >
                    <BarChart data={secondaryDistribution.data} theme={theme} color="#10b981" noDataLabel={text[language].previewNoChartData} />
                  </ChartCard>
                )}
              </div>
            </div>
          )}

          {/* Table */}
          <div>
            <h3 style={sectionTitleStyle(isDark, isRTL)}>{text[language].previewDataTable}</h3>
            {columns.length > 0 && (
              <DataTable
                key={category?.id}
                columns={columns}
                data={rows}
                theme={theme}
                isRTL={isRTL}
                onExportSelected={onExportSelected}
                onVisibleColumnsChange={onVisibleColumnsChange}
                labels={{
                  search: text[language].tableSearch, columns: text[language].tableColumns,
                  noResults: text[language].tableNoResults,
                  selected: text[language].tableSelected, exportSelected: text[language].tableExportSelected,
                  exportSelectedExcel: text[language].tableExportSelectedExcel, exportSelectedPdf: text[language].tableExportSelectedPdf,
                  page: text[language].tablePage, of: text[language].tableOf,
                }}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

const sectionTitleStyle = (isDark, isRTL) => ({
  fontSize: 14, fontWeight: 700, marginBottom: 12,
  color: isDark ? "#e2e8f0" : "#334155", textAlign: isRTL ? "right" : "left",
});

const ChartCard = ({ title, subtitle, icon: Icon, color, isDark, children, wide }) => {
  const accent = color || (isDark ? "#93c5fd" : "#2563eb");
  return (
    <div
      style={{
        gridColumn: wide ? "1 / -1" : "auto", padding: 18, borderRadius: 16, overflow: "hidden",
        background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)"}`,
        borderTop: `3px solid ${accent}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        {Icon && (
          <span
            style={{
              width: 30, height: 30, borderRadius: 9, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: `${accent}22`, color: accent,
            }}
          >
            <Icon size={15} />
          </span>
        )}
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: isDark ? "#f1f5f9" : "#0f172a", lineHeight: 1.3 }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 11, fontWeight: 500, color: isDark ? "rgba(255,255,255,0.45)" : "rgba(30,41,59,0.5)", marginTop: 1 }}>
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
};

export default ReportPreviewPage;
